/**
 * URL slug helpers — mirrors the Python ``slugify`` function in
 * ``backend/server.py`` so the tag URLs we emit on the frontend match
 * the slugs persisted in MongoDB (``db.tags.slug``) and the ones the
 * sitemap exposes to Googlebot.
 *
 * Why this exists:
 *   Google Search Console flagged hundreds of ``/tema/{name}`` URLs as
 *   "Validation failed" because we were emitting them with the raw
 *   tag name (``/tema/Texto Original`` → percent-encoded with spaces +
 *   uppercase). Different visit cases produced different URLs for the
 *   same theme, fragmenting PageRank. Switching to slugs gives every
 *   tag a single canonical URL: ``/tema/texto-original``.
 *
 * Keep this in sync with ``backend/server.py::slugify`` — same rules:
 *   1) NFKD normalise + drop diacritics
 *   2) Strip every non-[\w\s-] char
 *   3) Lowercase + trim
 *   4) Collapse runs of whitespace/_/- into a single hyphen
 */
export function tagSlug(name) {
  if (!name) return "";
  return String(name)
    .normalize("NFKD")
    // strip combining diacritical marks (NFD decomposes ç→c+◌̧, etc.)
    .replace(/[\u0300-\u036f]/g, "")
    // drop anything that isn't a word char, whitespace or hyphen
    .replace(/[^\w\s-]/g, "")
    .trim()
    .toLowerCase()
    // collapse runs of whitespace / underscore / hyphen into one hyphen
    .replace(/[\s_-]+/g, "-");
}

/**
 * Build the canonical ``/tema/<slug>`` URL for a tag name.
 * Always uses the slug form so we never emit the legacy
 * ``/tema/Texto Original`` (which Google flags as duplicate).
 */
export function tagHref(name) {
  return `/tema/${tagSlug(name)}`;
}
